import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Box } from '~/eds/src/components/Box/Box';
import { useDeleteModal } from '~/eds/src/components/hooks/useDeleteModal';
import { useDeletePermanentModal } from '~/eds/src/components/hooks/useDeletePermanentModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { Text } from '~/eds/src/components/Text/Text';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { api } from '~/redux/api/createApi';
import { Question } from '~/types/QuestionGroup';

import { useEditQuestionModal } from './useEditQuestionModal';

interface QuestionItemProps {
  question: Question;
  onQuestionClick: (question: Question) => void;
  disableActions?: boolean;
  disableRun?: boolean;
  status?: 'running' | 'complete' | 'pending' | 'idle';
}

export const QuestionItem = ({
  disableActions,
  disableRun,
  status,
  question,
  onQuestionClick,
}: QuestionItemProps) => {
  const intl = useIntl();
  const [isHovered, setIsHovered] = useState(false);
  // pending means that it's on the queue to be run
  const isRunning = status === 'running' || status === 'pending';
  const isComplete = status === 'complete';

  const modalTitle = intl.formatMessage({
    id: 'QuestionItem.deleteModalTitle',
    defaultMessage: 'Delete Saved Question',
    description: 'Title for delete question modal',
  });

  const [updateQuestion, { isLoading: isUpdating }] =
    api.endpoints.updateQuestion.useMutation();

  const [deleteQuestion, { isLoading: isDeleting }] =
    api.endpoints.deleteQuestion.useMutation();

  const { toast } = useToast();

  const [editModal, showEditModal] = useEditQuestionModal({ question });

  const handlePin = async () => {
    try {
      await updateQuestion({
        id: question.id,
        pinnedAt: question.pinnedAt ? null : new Date(),
      }).unwrap();
      toast({
        status: 'success',
        message: question.pinnedAt
          ? intl.formatMessage({
              id: 'QuestionItem.unpinnedSuccessMessage',
              defaultMessage: 'The question has been successfully unpinned.',
              description: 'Success message when question is unpinned',
            })
          : intl.formatMessage({
              id: 'QuestionItem.pinnedSuccessMessage',
              defaultMessage: 'The question has been successfully pinned.',
              description: 'Success message when question is pinned',
            }),
      });
    } catch (apiError: any) {
      toast({
        status: 'danger',
        message: question.pinnedAt
          ? intl.formatMessage(
              {
                id: 'QuestionItem.unpinErrorMessage',
                defaultMessage: 'The unpin action failed due to {errorTitle}.',
                description:
                  'Error message when unpin fails. Example: errorTitle = "Network Error"',
              },
              {
                errorTitle: apiError.errors[0].title,
              },
            )
          : intl.formatMessage(
              {
                id: 'QuestionItem.pinErrorMessage',
                defaultMessage: 'The pin action failed due to {errorTitle}.',
                description:
                  'Error message when pin fails. Example: errorTitle = "Network Error"',
              },
              {
                errorTitle: apiError.errors[0].title,
              },
            ),
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteQuestion({ id: question.id }).unwrap();
      toast({
        status: 'success',
        message: intl.formatMessage({
          id: 'QuestionItem.deleteSuccessMessage',
          defaultMessage: 'The question has been successfully deleted.',
          description: 'Success message when question is deleted',
        }),
      });
    } catch (apiError: any) {
      toast({
        status: 'danger',
        message: intl.formatMessage(
          {
            id: 'QuestionItem.deleteErrorMessage',
            defaultMessage: 'The delete action failed due to {errorTitle}.',
            description:
              'Error message when delete fails. Example: errorTitle = "Network Error"',
          },
          {
            errorTitle: apiError.errors[0].title,
          },
        ),
      });
    }
  };

  const [confirmationDeleteModal, showConfirmationDeleteModal] =
    useDeletePermanentModal({
      confirmText: 'DELETE',
      children: (
        <Text>
          {intl.formatMessage(
            {
              id: 'QuestionItem.confirmDeleteWithGroupsMessage',
              defaultMessage:
                "This question is also included in {count} question groups. Are you sure you want to delete the question you selected? You won't be able to undo this action.",
              description:
                'Confirmation message when deleting a question that belongs to groups. Example: count = 3',
            },
            {
              count: question.questionGroupIds.length,
            },
          )}
        </Text>
      ),
      title: modalTitle,
      onDelete: handleDelete,
      isDeleting,
    });

  const [deleteModal, showDeleteModal] = useDeleteModal({
    onDelete: handleDelete,
    isDeleting,
    title: modalTitle,
    children: (
      <Text>
        {intl.formatMessage({
          id: 'QuestionItem.confirmDeleteMessage',
          defaultMessage:
            "Are you sure you want to delete the 1 item you have selected? You won't be able to undo this action.",
          description: 'Confirmation message when deleting a question',
        })}
      </Text>
    ),
  });

  const questionName = question.name ?? question.text;
  const isNameTruncated = questionName.length > 100;

  const truncatedText = isNameTruncated
    ? `${questionName.slice(0, 100)}...`
    : questionName;

  // we should hide if there are any questions running but not if the actual question is running
  const shouldHideRun = disableRun && !isRunning;
  // we show if it's hoevered and not hidden or if the question is running
  const isRunButtonVisible =
    (isHovered && !shouldHideRun) || isRunning || isComplete;

  const runButtonText = isRunning
    ? intl.formatMessage({
        id: 'QuestionItem.runningText',
        defaultMessage: 'Running Question...',
        description: 'Text shown when question is running',
      })
    : isComplete
      ? intl.formatMessage({
          id: 'QuestionItem.completeText',
          defaultMessage: 'Running Complete',
          description: 'Text shown when question run is complete',
        })
      : intl.formatMessage({
          id: 'QuestionItem.runText',
          defaultMessage: 'Run Question',
          description: 'Text for run question button',
        });

  return (
    <Layout
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      align="flex-start"
      borderRadius="8px"
      p={4}
    >
      <Layout mr={4}>
        {isComplete ? (
          <Icon icon="check" color="status.success" />
        ) : isRunning ? (
          <LoadingSpinner mode="dashed" />
        ) : (
          <Icon icon="question" color="status.inactive" />
        )}
      </Layout>
      <Layout
        flex={1}
        direction="column"
        align="flex-start"
        role="button"
        data-eds-styles-disable-interactable
        onClick={() => {
          if (!disableRun && !isRunning) {
            onQuestionClick(question);
          }
        }}
      >
        <Tooltip tooltip={isNameTruncated ? questionName : ''}>
          <Text>{truncatedText}</Text>
        </Tooltip>
        <Box styles={{ visibility: isRunButtonVisible ? 'visible' : 'hidden' }}>
          <Text size="s" variant="tiny" color="action.link">
            {runButtonText}
          </Text>
        </Box>
      </Layout>
      {!disableActions && (
        <Layout align="flex-start" justify="flex-end" minW={38} minH={40}>
          {isHovered && !disableRun ? (
            <ActionsMenu
              disabled={isUpdating || isDeleting}
              direction="right"
              name="question item actions"
              enablePortal={true}
              actions={[
                {
                  label: question.pinnedAt
                    ? intl.formatMessage({
                        id: 'QuestionItem.unpinLabel',
                        defaultMessage: 'Unpin',
                        description: 'Label for unpin action',
                      })
                    : intl.formatMessage({
                        id: 'QuestionItem.pinLabel',
                        defaultMessage: 'Pin',
                        description: 'Label for pin action',
                      }),
                  value: question.pinnedAt ? 'unpin' : 'pin',
                  onClick: () => handlePin(),
                },
                {
                  label: intl.formatMessage({
                    id: 'QuestionItem.editLabel',
                    defaultMessage: 'Edit',
                    description: 'Label for edit action',
                  }),
                  value: 'edit',
                  onClick: showEditModal,
                },
                {
                  label: intl.formatMessage({
                    id: 'QuestionItem.deleteLabel',
                    defaultMessage: 'Delete',
                    description: 'Label for delete action',
                  }),
                  value: 'delete',
                  onClick: question.questionGroupIds.length
                    ? showConfirmationDeleteModal
                    : showDeleteModal,
                },
              ]}
            />
          ) : (
            question.pinnedAt && <Icon icon="pin" size="s" />
          )}
        </Layout>
      )}
      {question.questionGroupIds.length ? confirmationDeleteModal : deleteModal}
      {editModal}
    </Layout>
  );
};
